import {
  type ArtifactEventPayload,
  type ChangingKind,
  type ChatStepSlug,
  type DraftArtifactKind,
  type StudyStepSlug,
  type StudyStepView,
  isRunningStudyStep,
  studyStepArtifact,
  studyTypeName,
} from '@sigora/domain';

import type { AudienceCatalog, Study } from '../api/client.ts';
import { STEP_HELD_BY_CHECKOUT } from '../artifacts/launch-copy.ts';
import { ArtifactSidebar } from '../artifacts/Sidebar.tsx';
import type { BriefSynthesisRead } from '../artifacts/use-brief-synthesis.ts';
import { AnalyzingStep } from './AnalyzingStep.tsx';
import { AudienceStep } from './AudienceStep.tsx';
import type { BriefNews, BriefReadCache } from './brief/use-brief-read.ts';
import { BriefStep } from './BriefStep.tsx';
import { ChangeNotice, useStepConsequences } from './ChangeConsequences.tsx';
import { FieldingStep } from './FieldingStep.tsx';
import type { FollowUpScope } from './follow-ups/FollowUpThread.tsx';
import { GoalStep } from './GoalStep.tsx';
import { GuideStep } from './GuideStep.tsx';
import { MaterialsStep } from './MaterialsStep.tsx';
import { QuestionsStep } from './QuestionsStep.tsx';
import { StudyTypePicker } from './StudyTypePicker.tsx';

/**
 * What a change on each step changes, for the steps whose change something below them depends on.
 * The audience has nothing below it, so its step has no notice of what a change would affect.
 */
const CHANGING_BY_STEP: Partial<Record<StudyStepView['slug'], ChangingKind>> = {
  study_type: 'study_type',
  materials: 'materials',
  goal: 'goal',
  questions: 'rqs',
  guide: 'dg',
};

/**
 * Whether a launch freezes this step's content, so the screen can say so before the reader tries:
 * every step that sets the study up, which is every step but the ones that run once it is launched.
 */
const isInputStep = (slug: StudyStepSlug): boolean => !isRunningStudyStep(slug);

export interface StepPanelProps {
  step: StudyStepView;
  studyId: string;
  study: Study | null;
  streamed: readonly ArtifactEventPayload[];
  audienceCatalog: AudienceCatalog | null;
  refreshToken: number;
  onStudyChanged: () => void;
  onApproved: (approved: { kind: DraftArtifactKind; turnId: string | null }) => void;
  /** Opens another step of this study, which the Launch step's blockers link to. */
  onOpenStep?: (stepSlug: StudyStepSlug) => void;
  /**
   * Puts words in a step's composer when it is empty and brings the composer to the reader, which
   * is how the Brief's Ask about this reaches the Brief's conversation below it, with the card the
   * question is about.
   */
  offer: (step: ChatStepSlug, text: string, scope?: FollowUpScope) => void;
  /** The study's Brief once read, which the Brief keeps across visits. */
  briefCache: BriefReadCache;
  /** The study's Brief request while it is analysed, which the Analyzing step shows. */
  brief: BriefSynthesisRead;
  /** What the Brief's follow-ups say moved the Brief, which the Brief step takes. */
  briefNews?: BriefNews | null;
}

/**
 * What one step shows.
 *
 * A step with nothing built behind it says so plainly rather than being hidden or skipped: the
 * reader can see the whole flow, count the ten, and know which parts of it exist. The rest hand
 * the study's own surfaces the step they belong to.
 */
export const StepPanel = ({
  step,
  studyId,
  study,
  streamed,
  audienceCatalog,
  refreshToken,
  onStudyChanged,
  onApproved,
  onOpenStep,
  offer,
  briefCache,
  brief,
  briefNews = null,
}: StepPanelProps) => {
  const consequences = useStepConsequences({
    studyId,
    study,
    changing: CHANGING_BY_STEP[step.slug] ?? null,
    refreshToken,
  });

  if (step.status === 'unimplemented') {
    return (
      <section className="step-panel" data-test-id="step-unimplemented">
        <p className="step-unbuilt">{step.name} is not built yet.</p>
        <p>{step.about}</p>
        <p>
          Nothing here has been done or skipped. The step is part of the study and is waiting on the
          work that builds it; carry on to the next one.
        </p>
      </section>
    );
  }

  /**
   * Why this step's content cannot change, or nothing while it can.
   *
   * Four facts close the documents and they happen in order: a purchase holding them while its
   * checkout is open, the payment, the freeze partway through the set-up run, and the launch once
   * Remesh confirms. Only the last of them means the study is out in the world, so only it may say
   * so — a study frozen and waiting on Remesh has not launched, whatever its documents can no
   * longer do. An open checkout is the one hold that can end with the documents editable again,
   * when the checkout is cancelled or closes unpaid, so it says what it is waiting on.
   */
  const frozen = !isInputStep(step.slug)
    ? null
    : study?.launchedAt != null
      ? 'This study has launched, so nothing on this step can change.'
      : study?.lockedAt != null
        ? 'This study is frozen while it is set up, so nothing on this step can change.'
        : study?.paidAt != null || study?.payment.kind === 'paid'
          ? 'This study is paid for, so nothing on this step can change.'
          : study?.payment.editingHeld === true
            ? STEP_HELD_BY_CHECKOUT
            : null;
  /**
   * Why the step cannot change, or — on a step it can — what a change there would affect below it,
   * above the step's own controls.
   */
  const notice =
    frozen === null ? (
      <ChangeNotice consequences={consequences} />
    ) : (
      <p className="step-frozen" data-test-id="step-read-only" role="note">
        {frozen}
      </p>
    );

  /**
   * The three steps whose body is not a draft document: a choice, the Materials and the Goal.
   *
   * Ahead of the artifact fall-through below, which answers `launch` for a step it has no
   * document for — so a step that is built and document-less has to be named here or it is drawn
   * as the price panel.
   */
  if (step.slug === 'study_type') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        {notice}

        <StudyTypePicker
          studyId={studyId}
          selection={study?.studyType ?? null}
          suggestion={study?.studyTypeSuggestion ?? null}
          onStudyChanged={onStudyChanged}
        />
      </section>
    );
  }

  if (step.slug === 'materials') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        {notice}

        <MaterialsStep
          studyId={studyId}
          studyType={study?.studyType ?? null}
          frozen={frozen !== null}
          refreshToken={refreshToken}
          onStudyChanged={onStudyChanged}
        />
      </section>
    );
  }

  if (step.slug === 'goal') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        {notice}

        <GoalStep
          studyId={studyId}
          frozen={frozen !== null}
          refreshToken={refreshToken}
          onStudyChanged={onStudyChanged}
        />
      </section>
    );
  }

  /**
   * The research questions, one card each, drafted on arrival from the chosen Goal. Ahead of the
   * artifact fall-through, which would draw them as one generic document card.
   */
  if (step.slug === 'questions') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        {notice}

        <QuestionsStep
          studyId={studyId}
          frozen={frozen !== null}
          refreshToken={refreshToken}
          onStudyChanged={onStudyChanged}
          onApproved={onApproved}
        />
      </section>
    );
  }

  /**
   * The discussion guide, section by section as participants will see it, drafted on arrival from
   * the approved research questions. Ahead of the artifact fall-through, which would draw it as one
   * generic document card.
   */
  if (step.slug === 'guide') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        {notice}

        <GuideStep
          studyId={studyId}
          frozen={frozen !== null}
          refreshToken={refreshToken}
          onStudyChanged={onStudyChanged}
          onApproved={onApproved}
        />
      </section>
    );
  }

  /**
   * The audience: who Sigora will find, the screening questions and the Limitations, drafted when
   * the guide is approved. Ahead of the artifact fall-through, which would draw it as one generic
   * document card.
   */
  if (step.slug === 'audience') {
    const typeId = study?.studyType?.id;

    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        {notice}

        <AudienceStep
          studyId={studyId}
          studyTypeName={typeId === undefined ? null : (studyTypeName(typeId) ?? null)}
          frozen={frozen !== null}
          audienceCatalog={audienceCatalog}
          refreshToken={refreshToken}
          onStudyChanged={onStudyChanged}
          onApproved={onApproved}
        />
      </section>
    );
  }

  /**
   * What the launched study is doing, and the watch that reads it again while it fields. Ahead of
   * the artifact fall-through, which would draw it as the price panel.
   */
  if (step.slug === 'fielding') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        <FieldingStep study={study} onStudyChanged={onStudyChanged} />
      </section>
    );
  }

  /**
   * The answers being read and the Brief written, and the watch that reads the study again
   * meanwhile. Ahead of the artifact fall-through, which would draw it as the price panel.
   */
  if (step.slug === 'analyzing') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        <AnalyzingStep
          studyId={studyId}
          study={study}
          onStudyChanged={onStudyChanged}
          brief={brief}
        />
      </section>
    );
  }

  /**
   * The Brief, which reads the published Brief rather than a draft document. Ahead of the artifact
   * fall-through, which would draw it as the price panel.
   */
  if (step.slug === 'brief') {
    return (
      <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
        <BriefStep
          studyId={studyId}
          briefCache={briefCache}
          offer={offer}
          refreshToken={refreshToken}
          briefNews={briefNews}
        />
      </section>
    );
  }

  const kind = studyStepArtifact(step.slug);

  return (
    <section className="step-panel" data-test-id={`step-panel-${step.slug}`}>
      {notice}

      <ArtifactSidebar
        studyId={studyId}
        study={study}
        streamed={streamed}
        audienceCatalog={audienceCatalog}
        refreshToken={refreshToken}
        onStudyChanged={onStudyChanged}
        onApproved={onApproved}
        only={kind ?? 'launch'}
        onOpenStep={onOpenStep}
      />
    </section>
  );
};
